import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Projects() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", client_name: "", description: "", budget: "", deadline: "" });

  const load = async () => setItems(await api.listProjects());
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title || !form.client_name) return toast.error("Title and client required");
    try {
      await api.createProject({ ...form, budget: Number(form.budget) || 0 });
      setOpen(false);
      setForm({ title: "", client_name: "", description: "", budget: "", deadline: "" });
      await load();
      toast.success("Project created");
    } catch { toast.error("Could not create"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete project?")) return;
    await api.deleteProject(id);
    await load();
  };

  return (
    <div className="space-y-6" data-testid="projects">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="mono text-white/40">FACTORY · PROJECTS</div>
          <h1 className="text-4xl font-extrabold mt-2">All projects</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-project-btn" className="kinetic-btn rounded-sm"><Plus className="w-4 h-4 mr-2" /> New Project</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0C0C0C] border border-white/10 text-white rounded-sm">
            <DialogHeader><DialogTitle className="font-sans">New Project</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {[
                ["title", "Title"], ["client_name", "Client"], ["budget", "Budget (INR)"], ["deadline", "Deadline"],
              ].map(([k, l]) => (
                <div key={k}>
                  <Label className="mono text-white/60">{l}</Label>
                  <Input data-testid={`field-${k}`} value={form[k]} onChange={(e) => setForm(f => ({ ...f, [k]: e.target.value }))}
                    className="bg-black border-white/15 rounded-sm" />
                </div>
              ))}
              <div>
                <Label className="mono text-white/60">Description</Label>
                <Textarea data-testid="field-description" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                  className="bg-black border-white/15 rounded-sm min-h-24" />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="create-project-submit" onClick={create} className="kinetic-btn rounded-sm">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.length === 0 && <div className="col-span-full mono text-white/40 border border-dashed border-white/15 p-12 text-center">NO PROJECTS · Click &quot;+ New Project&quot; to start</div>}
        {items.map(p => (
          <div key={p.id} className="group border border-white/10 bg-[#0C0C0C] hover:border-kinetic transition relative">
            <Link to={`/app/factory/projects/${p.id}`} className="block p-5" data-testid={`project-card-${p.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="mono text-white/40">{p.client_name}</div>
                  <div className="font-sans font-bold text-xl mt-1">{p.title}</div>
                </div>
                <span className="stage-pill border-white/20 text-white/70">{p.stage}</span>
              </div>
              <p className="text-white/60 mt-3 line-clamp-2">{p.description || "No description"}</p>
              <div className="flex items-center justify-between mt-5">
                <div className="mono text-white/50">₹{(p.budget || 0).toLocaleString('en-IN')}</div>
                <div className="flex items-center gap-1">
                  {["50%", "25%", "25%"].map((label, i) => {
                    const on = [p.milestone_advance, p.milestone_mid, p.milestone_final][i];
                    return <span key={i} className={`mono px-1.5 py-0.5 ${on ? 'bg-neon text-black' : 'bg-white/5 text-white/40'}`}>{label}</span>;
                  })}
                </div>
              </div>
            </Link>
            <button data-testid={`del-${p.id}`} onClick={() => del(p.id)} className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition text-white/40 hover:text-coral">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
